import React, { useCallback, useEffect, useState } from 'react';
import { Package, Clock, CheckCircle, XCircle, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../context/AdminContext';

const STATUS_CONFIG = {
  pending:    { label: 'Pending',    color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  confirmed:  { label: 'Confirmed',  color: 'bg-blue-100 text-blue-700',     icon: CheckCircle },
  processing: { label: 'Processing', color: 'bg-purple-100 text-purple-700', icon: Package },
  completed:  { label: 'Completed',  color: 'bg-green-100 text-green-700',   icon: CheckCircle },
  cancelled:  { label: 'Cancelled',  color: 'bg-red-100 text-red-700',       icon: XCircle },
};

const PAYMENT_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
  paid:    { label: 'Paid',    color: 'bg-green-100 text-green-700' },
  failed:  { label: 'Failed',  color: 'bg-red-100 text-red-700' },
};

const AdminOrders = () => {
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await adminApi.get(`/orders${params}`);
      setOrders(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load orders', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filter, toast]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const updateOrderStatus = async (orderId, status) => {
    setUpdatingStatus(true);
    try {
      const response = await adminApi.put(`/orders/${orderId}/status`, { status });
      setSelectedOrder(response.data);
      toast({ title: 'Success', description: `Order status updated to ${status}` });
      loadOrders();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const updatePaymentStatus = async (orderId, payment_status) => {
    setUpdatingStatus(true);
    try {
      const response = await adminApi.put(`/orders/${orderId}/status`, { payment_status });
      setSelectedOrder(response.data);
      toast({ title: 'Success', description: `Payment status updated to ${payment_status}` });
      loadOrders();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update payment', variant: 'destructive' });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const statusCounts = orders.reduce((acc, o) => {
    acc[o.status] = (acc[o.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="admin-orders-page">
      <div className="mb-6">
        <h2 className="text-xl sm:text-3xl font-bold text-gray-900">Orders Management</h2>
        <p className="text-gray-600 mt-1">View and manage customer orders</p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-6">
        {[
          { key: 'all', label: 'All Orders', count: orders.length, color: 'bg-gray-100 text-gray-700' },
          { key: 'pending', label: 'Pending', count: statusCounts.pending || 0, color: 'bg-yellow-100 text-yellow-700' },
          { key: 'confirmed', label: 'Confirmed', count: statusCounts.confirmed || 0, color: 'bg-blue-100 text-blue-700' },
          { key: 'processing', label: 'Processing', count: statusCounts.processing || 0, color: 'bg-purple-100 text-purple-700' },
          { key: 'completed', label: 'Completed', count: statusCounts.completed || 0, color: 'bg-green-100 text-green-700' },
        ].map(s => (
          <button
            key={s.key}
            onClick={() => setFilter(s.key)}
            className={`p-3 rounded-lg text-center transition-all ${filter === s.key ? 'ring-2 ring-red-500 shadow' : 'hover:shadow'} ${s.color}`}
            data-testid={`filter-${s.key}`}
          >
            <div className="text-2xl font-bold">{s.count}</div>
            <div className="text-xs">{s.label}</div>
          </button>
        ))}
      </div>

      {/* Order Detail Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={(open) => { if (!open) setSelectedOrder(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedOrder && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  Order {selectedOrder.order_number}
                  <Badge className={STATUS_CONFIG[selectedOrder.status]?.color}>
                    {STATUS_CONFIG[selectedOrder.status]?.label}
                  </Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                {/* Customer Info */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Customer</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm bg-gray-50 rounded-lg p-3">
                    <div><span className="text-gray-500">Name:</span> {selectedOrder.customer.name}</div>
                    <div><span className="text-gray-500">Email:</span> {selectedOrder.customer.email}</div>
                    <div><span className="text-gray-500">Phone:</span> {selectedOrder.customer.phone}</div>
                    <div><span className="text-gray-500">Address:</span> {selectedOrder.customer.address}</div>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Items</h4>
                  <div className="border rounded-lg divide-y">
                    {selectedOrder.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 text-sm">
                        <div>
                          <span className="font-medium">{item.product_name}</span>
                          <span className="text-gray-500 ml-2">x{item.quantity}</span>
                        </div>
                        <span className="font-semibold">{(item.price * item.quantity).toFixed(3)} BHD</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center p-3 font-bold bg-gray-50">
                      <span>Total</span>
                      <span className="text-red-600">{selectedOrder.total.toFixed(3)} BHD</span>
                    </div>
                  </div>
                </div>

                {selectedOrder.notes && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Notes</h4>
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{selectedOrder.notes}</p>
                  </div>
                )}

                {/* Status Actions */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Update Status</h4>
                  <div className="flex flex-wrap gap-2">
                    {['pending', 'confirmed', 'processing', 'completed', 'cancelled'].map(s => (
                      <Button
                        key={s}
                        size="sm"
                        variant={selectedOrder.status === s ? 'default' : 'outline'}
                        className={selectedOrder.status === s ? 'bg-red-600 hover:bg-red-700' : ''}
                        onClick={() => updateOrderStatus(selectedOrder.id, s)}
                        disabled={updatingStatus || selectedOrder.status === s}
                        data-testid={`set-status-${s}`}
                      >
                        {STATUS_CONFIG[s]?.label}
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Payment Status</h4>
                  <div className="flex gap-2">
                    {['pending', 'paid', 'failed'].map(ps => (
                      <Button
                        key={ps}
                        size="sm"
                        variant={selectedOrder.payment_status === ps ? 'default' : 'outline'}
                        className={selectedOrder.payment_status === ps ? 'bg-red-600 hover:bg-red-700' : ''}
                        onClick={() => updatePaymentStatus(selectedOrder.id, ps)}
                        disabled={updatingStatus || selectedOrder.payment_status === ps}
                        data-testid={`set-payment-${ps}`}
                      >
                        {PAYMENT_CONFIG[ps]?.label}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="text-xs text-gray-400">
                  Created: {new Date(selectedOrder.created_at).toLocaleString()} | Updated: {new Date(selectedOrder.updated_at).toLocaleString()}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Orders Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <Package className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg">No orders found</p>
          <p className="text-sm mt-1">{filter !== 'all' ? 'Try a different filter' : 'Orders will appear here once customers place them'}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3 font-semibold text-gray-600">Order #</th>
                <th className="text-left p-3 font-semibold text-gray-600">Customer</th>
                <th className="text-left p-3 font-semibold text-gray-600">Items</th>
                <th className="text-left p-3 font-semibold text-gray-600">Total</th>
                <th className="text-left p-3 font-semibold text-gray-600">Status</th>
                <th className="text-left p-3 font-semibold text-gray-600">Payment</th>
                <th className="text-left p-3 font-semibold text-gray-600">Date</th>
                <th className="text-right p-3 font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {orders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50 transition-colors" data-testid={`order-row-${order.id}`}>
                  <td className="p-3 font-mono font-semibold text-sm">{order.order_number}</td>
                  <td className="p-3">
                    <div className="font-medium">{order.customer.name}</div>
                    <div className="text-xs text-gray-500">{order.customer.phone}</div>
                  </td>
                  <td className="p-3 text-gray-600">{order.items.length} item{order.items.length > 1 ? 's' : ''}</td>
                  <td className="p-3 font-semibold">{order.total.toFixed(3)} BHD</td>
                  <td className="p-3">
                    <Badge className={`${STATUS_CONFIG[order.status]?.color} text-xs`}>{STATUS_CONFIG[order.status]?.label}</Badge>
                  </td>
                  <td className="p-3">
                    <Badge className={`${PAYMENT_CONFIG[order.payment_status]?.color} text-xs`}>{PAYMENT_CONFIG[order.payment_status]?.label}</Badge>
                  </td>
                  <td className="p-3 text-xs text-gray-500">{new Date(order.created_at).toLocaleDateString()}</td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => setSelectedOrder(order)} data-testid={`view-order-${order.id}`}>
                      <Eye className="h-4 w-4 mr-1" />View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
